import { Box, Paper } from '@mui/material';
import { useState } from 'react';
import StackablePageButtons from '@product-framework/components/StackablePageButtons';
import { IconLibrary } from '@product-framework/index';
import {
  useDataProvider,
  useLocalization,
  useSnackbar,
} from '@ui-framework/index';
import { nanoid } from 'nanoid';
import DefaultSchedulesList from '../components/DefaultSchedulesList/index';
import DefaultScheduleForm from '../components/DefaultScheduleForm';
import DefaultScheduleDetailView from '../components/DefaultScheduleDetailView';
import { CURRENT_VIEW, DEFAULT_SCHEDULE_CURRENT_ID } from '../utils/constants';

function DefaultSchedules({ serviceProviderId, locationId = null }) {
  const snackbar = useSnackbar();
  const { formatMessage } = useLocalization();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { put, get, post, remove, getApiUrl } = useDataProvider(provider);

  const [selectedScheduleId, setSelectedScheduleId] = useState(
    DEFAULT_SCHEDULE_CURRENT_ID,
  );
  const [listRefreshKey, setlistRefreshKey] = useState(nanoid());
  const [currentView, setCurrentView] = useState(CURRENT_VIEW.DEFAULT);

  const commonParams = {
    snackbar,
    put,
    get,
    post,
    remove,
    getApiUrl,
    pageId: serviceProviderId,
  };

  const FEATURE_VIEW_CURRENT_CONSOLIDATED_VIEW = false;

  const stackablePageButtons = [
    ...(FEATURE_VIEW_CURRENT_CONSOLIDATED_VIEW
      ? [
          {
            label: formatMessage({
              id: 'VIEW_CURRENT_DEFAULT_SCHEDULE',
              defaultMessage: 'View Current Default Schedule',
            }),
            Icon: IconLibrary.CurrentDefaultSchedule,
            variant: 'contained',
            permissionName: 'CAN_CREATE_SP_AVAILABILITY_DEFAULT_SCHEDULE',
            onClick: () => {
              setSelectedScheduleId(DEFAULT_SCHEDULE_CURRENT_ID);
              setCurrentView(CURRENT_VIEW.VIEW_SCHEDULE);
            },
          },
        ]
      : []),
    ...(currentView !== CURRENT_VIEW.EDIT_SCHEDULE
      ? [
          {
            label: formatMessage({
              id: 'CREATE_DEFAULT_SCHEDULE',
              defaultMessage: 'Create Default Schedule',
            }),
            Icon: IconLibrary.DefaultSchedule,
            variant: 'contained',
            permissionName: 'CAN_CREATE_SP_AVAILABILITY_DEFAULT_SCHEDULE',
            onClick: () => {
              setSelectedScheduleId(null);
              setCurrentView(CURRENT_VIEW.EDIT_SCHEDULE);
            },
          },
        ]
      : []),
    ...(currentView !== CURRENT_VIEW.DEFAULT
      ? [
          {
            label: formatMessage({
              id: 'LIST_DEFAULT_SCHEDULES',
              defaultMessage: 'List Default Schedules',
            }),
            Icon: IconLibrary.DefaultSchedules,
            variant: 'outlined',
            permissionName: 'CAN_VIEW_SP_AVAILABILITY_DEFAULT_SCHEDULE',
            onClick: () => {
              setSelectedScheduleId(null);
              setCurrentView(CURRENT_VIEW.DEFAULT);
            },
          },
        ]
      : []),
  ];

  const refreshListView = () => {
    setlistRefreshKey(nanoid());
  };

  return (
    <Box>
      <Box sx={{ mt: 2 }}>
        <StackablePageButtons
          backButton={false}
          buttons={stackablePageButtons}
        />
      </Box>

      {[CURRENT_VIEW.VIEW_SCHEDULE].includes(currentView) && (
        <Paper variant="outlined" sx={{ mb: 2, mt: 2 }}>
          <DefaultScheduleDetailView
            serviceProviderId={serviceProviderId}
            params={commonParams}
            currentView={currentView}
            setCurrentView={setCurrentView}
            selectedScheduleId={selectedScheduleId}
            setSelectedScheduleId={setSelectedScheduleId}
            locationId={locationId}
          />
        </Paper>
      )}
      {[CURRENT_VIEW.EDIT_SCHEDULE].includes(currentView) && (
        <Paper variant="outlined" sx={{ mb: 2, mt: 2 }}>
          <DefaultScheduleForm
            params={commonParams}
            currentView={currentView}
            setCurrentView={setCurrentView}
            selectedScheduleId={selectedScheduleId}
            setSelectedScheduleId={setSelectedScheduleId}
            refreshListView={refreshListView}
            locationId={locationId}
          />
        </Paper>
      )}
      {[CURRENT_VIEW.DEFAULT].includes(currentView) && (
        <Paper variant="outlined" sx={{ mb: 2, mt: 2 }}>
          <DefaultSchedulesList
            key={listRefreshKey}
            params={commonParams}
            currentView={currentView}
            setCurrentView={setCurrentView}
            selectedScheduleId={selectedScheduleId}
            setSelectedScheduleId={setSelectedScheduleId}
            refreshListView={refreshListView}
            locationId={locationId}
          />
        </Paper>
      )}
    </Box>
  );
}

export default DefaultSchedules;
